/**
 * Blocked email domains — free consumer providers + disposable / temp-mail services.
 *
 * Policy: only "professional" (corporate / work) email addresses may enroll for
 * an account. Free webmail and throwaway domains are rejected client-side and
 * re-validated server-side (defense-in-depth — OWASP A04 Insecure Design).
 *
 * KEEP IN SYNC with the server-side copy in:
 *   - src/lambda/account-request-handler.js  (BLOCKED_EMAIL_DOMAINS)
 *   - lambda-deploy/account-request-handler.js
 *   - src/lambda/input-validator.js
 *
 * If a legitimate small business or sole proprietor is blocked, support can
 * whitelist them out-of-band; this list intentionally errs on the strict side.
 */

export const BLOCKED_EMAIL_DOMAINS = new Set([
  // ── Free consumer webmail ────────────────────────────────────────────────
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de', 'yahoo.es',
  'yahoo.ca', 'yahoo.in', 'yahoo.com.br', 'yahoo.com.mx', 'ymail.com', 'rocketmail.com',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.es', 'hotmail.it',
  'outlook.com', 'outlook.fr', 'outlook.de', 'outlook.es', 'outlook.com.br',
  'live.com', 'live.co.uk', 'live.fr',
  'msn.com',
  'aol.com', 'aim.com',
  'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me', 'pm.me',
  'gmx.com', 'gmx.de', 'gmx.net', 'gmx.us',
  'mail.com', 'mail.ru', 'mail.de',
  'yandex.com', 'yandex.ru',
  'zoho.com',
  'fastmail.com', 'fastmail.fm',
  'tutanota.com', 'tutanota.de', 'tutamail.com', 'tuta.io',
  'hushmail.com',
  'qq.com', '163.com', '126.com', 'sina.com', 'sina.cn',
  'naver.com', 'hanmail.net', 'daum.net',
  'rediffmail.com',
  'inbox.com', 'lycos.com',
  'hey.com',
  'duck.com', 'duckduckgo.com',

  // ── Disposable / temp-mail ───────────────────────────────────────────────
  'mailinator.com', 'mailinator.net', 'mailinator2.com',
  'tempmail.com', 'temp-mail.org', 'tempmail.net', 'tempmailaddress.com',
  '10minutemail.com', '10minutemail.net', '20minutemail.com',
  'guerrillamail.com', 'guerrillamail.net', 'guerrillamail.org', 'guerrillamail.biz',
  'yopmail.com', 'yopmail.fr', 'yopmail.net',
  'throwaway.email', 'throwawaymail.com',
  'trashmail.com', 'trashmail.net', 'trashmail.de',
  'sharklasers.com', 'grr.la',
  'getnada.com', 'nada.email',
  'dispostable.com',
  'maildrop.cc',
  'mintemail.com',
  'mohmal.com',
  'fakeinbox.com',
  'mailnesia.com',
  'mytemp.email',
  'spamgourmet.com',
  'mail-temporaire.fr', 'jetable.org',
  'dropmail.me',
  'inboxbear.com', 'tempinbox.com',
  'fakemail.net',
  'mailcatch.com',
  'spambog.com', 'spambog.de',
  'mvrht.net',
  'emailondeck.com',
  'mailtemp.info',
  'temporary-mail.net',
  'discard.email', 'discardmail.com',
  'mail7.io',
  'wegwerfmail.de', 'wegwerfemail.de',
  'incognitomail.com', 'incognitomail.net'
]);

/**
 * Returns the lowercased domain portion of an email, or '' if malformed.
 */
export function getEmailDomain(email) {
  if (typeof email !== 'string') return '';
  const at = email.lastIndexOf('@');
  if (at < 0 || at === email.length - 1) return '';
  return email.slice(at + 1).trim().toLowerCase();
}

/**
 * True if the email's domain is on the blocklist (free or disposable provider).
 */
export function isFreeOrDisposableEmail(email) {
  const domain = getEmailDomain(email);
  return !!domain && BLOCKED_EMAIL_DOMAINS.has(domain);
}
